import { useMutation } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";

import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { changeStripeBillingEmail } from "./billing-api";
import { TextField } from "./billing-fields";
import {
  fieldState,
  REASON_REQUIREMENT,
  useFormErrors,
  type FieldGuide,
} from "./billing-validation";

const EMAIL_GUIDE: FieldGuide = {
  rename: (field) => (field === "billingEmail" ? "email" : field),
  requirements: {
    email: "Where Stripe emails invoices and receipts, like ap@customer.example.",
    reason: REASON_REQUIREMENT,
  },
};

/**
 * Where Stripe emails a linked customer's invoices. It starts from the current address, or the
 * primary contact's when there is none, and saving changes Stripe and the platform together.
 */
export function BillingEmailForm({
  customerSlug,
  current,
  suggested,
  onDone,
  onCancel,
}: {
  customerSlug: string;
  current: string | null;
  suggested: string | null;
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  // Untouched, the field follows the current address or the primary contact's, which may load late.
  const [typed, setEmail] = useState<string | null>(null);
  const email = typed ?? current ?? suggested ?? "";
  const [reason, setReason] = useState("");
  const errors = useFormErrors(EMAIL_GUIDE);
  const save = useMutation({
    mutationFn: () =>
      changeStripeBillingEmail(customerSlug, { billingEmail: email.trim(), reason: reason.trim() }),
    onSuccess: onDone,
    onError: errors.fail,
  });
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate();
  }
  return (
    <form
      className="mt-5 grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-start"
      onSubmit={submit}
      aria-label="Billing email"
      noValidate
    >
      <TextField
        {...fieldState(errors, EMAIL_GUIDE.requirements, "email", "billing-email")}
        label="Billing email"
        type="email"
        value={email}
        required
        onChange={(value) => {
          errors.clear("email");
          setEmail(value);
        }}
      />
      <TextField
        {...fieldState(errors, EMAIL_GUIDE.requirements, "reason", "billing-email-reason")}
        label="Reason"
        value={reason}
        required
        maxLength={500}
        onChange={(value) => {
          errors.clear("reason");
          setReason(value);
        }}
      />
      <div className="flex gap-2 sm:pt-7">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={save.isPending}>
          Save billing email
        </Button>
      </div>
      {errors.summary ? <FieldError className="sm:col-span-3">{errors.summary}</FieldError> : null}
    </form>
  );
}
